import React from 'react'
import cx from 'classnames'
import { Link as ReactRouterLink } from 'react-router-dom'
import * as Icon from '@manychat/icons'

import { AdoptionComponent } from '../../../constants/AdoptionComponent'
import tints from '../../../constants/tints.json'
import { useDsAdoptionAttribute } from '../../../utils/dsAdoption'
import { fixedForwardRef } from '../../../utils/fixedForwardRef'

import {
  As,
  MainNavigationItemAsAnchorProps,
  MainNavigationItemAsButtonProps,
  MainNavigationItemAsDivProps,
  MainNavigationItemAsRouterLinkProps,
  MainNavigationItemProps,
} from './interfaces'

import cm from './MainNavigationItem.module.css'

function isAnchorProps(props: { as?: As }): props is MainNavigationItemAsAnchorProps<'a'> {
  return props.as === 'a'
}

function isRouterLinkProps(props: {
  as?: As
}): props is MainNavigationItemAsRouterLinkProps<typeof ReactRouterLink> {
  return props.as === ReactRouterLink
}

function isButtonProps(props: { as?: As }): props is MainNavigationItemAsButtonProps<'button'> {
  return props.as === 'button'
}

function isDivProps(props: { as?: As }): props is MainNavigationItemAsDivProps<'div'> {
  return props.as === 'div'
}

const MainNavigationItemComponent = <T extends As = 'a'>(
  props: MainNavigationItemProps<T>,
  ref: React.ForwardedRef<HTMLElement>,
) => {
  const {
    icon,
    collapsed,
    hasNotification,
    notificationCount,
    notificationColor,
    selected,
    className,
    children,
    tooltip,
    ariaLabel,
    ...restProps
  } = props

  const adoptionAttribute = useDsAdoptionAttribute(AdoptionComponent.MAIN_NAVIGATION_ITEM)
  const innerElementClassname = cx(cm.mainNavItemLink, { [cm.selected]: selected })
  const IconElement = typeof icon === 'string' && Icon[icon as keyof typeof Icon]
  const VisualElement = IconElement ? React.createElement(IconElement, { size: 24 }) : icon
  const defaultNotificationColor = tints.blue[3]

  const showCounterDot = hasNotification && (collapsed || !notificationCount)
  const showNotificationCount = hasNotification && Boolean(notificationCount)

  const content = (
    <>
      {icon && (
        <span className={cm.visualElement} aria-hidden="true">
          {VisualElement}
          {showCounterDot && (
            <span
              className={cm.dot}
              style={{
                backgroundColor: notificationColor || defaultNotificationColor,
              }}
            />
          )}
        </span>
      )}
      {!collapsed && (
        <>
          <span className={cm.label}>{children}</span>
          {showNotificationCount && (
            <span
              className={cx(cm.labelCounter, 'text-sm')}
              aria-label={`${notificationCount}-notifications`}
            >
              {String(notificationCount)}
            </span>
          )}
        </>
      )}
    </>
  )

  let mainNavigationInnerElement = null
  const current = selected ? 'page' : undefined

  if (isAnchorProps(restProps)) {
    const { as: _, ...anchorProps } = restProps as MainNavigationItemAsAnchorProps<'a'>

    mainNavigationInnerElement = (
      <a
        className={innerElementClassname}
        {...anchorProps}
        aria-label={ariaLabel}
        aria-current={current}
        role="menuitem"
        ref={ref as typeof restProps['ref']}
      >
        {content}
      </a>
    )
  }

  if (isRouterLinkProps(restProps)) {
    const { as: _, ...linkProps } = restProps as MainNavigationItemAsRouterLinkProps<
      typeof ReactRouterLink
    >

    mainNavigationInnerElement = (
      <ReactRouterLink
        className={innerElementClassname}
        {...linkProps}
        aria-label={ariaLabel}
        aria-current={current}
        role="menuitem"
        ref={ref as typeof restProps['ref']}
      >
        {content}
      </ReactRouterLink>
    )
  }

  if (isButtonProps(restProps)) {
    const { as: _, ...buttonProps } = restProps as MainNavigationItemAsButtonProps<
      typeof ReactRouterLink
    >

    mainNavigationInnerElement = (
      <button
        className={innerElementClassname}
        {...buttonProps}
        aria-label={ariaLabel}
        aria-current={current}
        role="menuitem"
        ref={ref as typeof restProps['ref']}
      >
        {content}
      </button>
    )
  }

  if (isDivProps(restProps)) {
    const { as: _, ...buttonProps } = restProps as MainNavigationItemAsDivProps<'div'>

    mainNavigationInnerElement = (
      <div
        className={innerElementClassname}
        {...buttonProps}
        aria-label={ariaLabel}
        aria-current={current}
        role="menuitem"
        tabIndex={0}
        ref={ref as typeof restProps['ref']}
      >
        {content}
      </div>
    )
  }

  const name = collapsed && tooltip ? tooltip : undefined
  return (
    <li
      className={cx(cm.mainNavItem, { [cm.collapsed]: collapsed }, className)}
      data-title={name}
      data-title-at="right"
      aria-label={name}
      role="presentation"
      {...adoptionAttribute}
    >
      {mainNavigationInnerElement}
    </li>
  )
}

MainNavigationItemComponent.displayName = 'MainNavigation.Item'

export const MainNavigationItem = fixedForwardRef(MainNavigationItemComponent)
